import { Component, EventEmitter, Output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { ChipModule } from 'primeng/chip';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { ClientService } from '../../../../service/client.service';
import { ClientDetails } from '../../../models/client.model';
import { TextareaModule } from 'primeng/textarea';

@Component({
  selector: 'app-add-client-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    DialogModule,
    ButtonModule,
    InputTextModule,
    TextareaModule,
    SelectModule,
    ChipModule,
    ToastModule
  ],
  providers: [MessageService],
  template: `
    <p-dialog 
      header="Add New Client" 
      [(visible)]="visible" 
      [modal]="true" 
      [style]="{ width: '650px' }"
      [draggable]="false"
      [resizable]="false"
      (onHide)="onHide()">
      
      <form [formGroup]="clientForm" (ngSubmit)="onSubmit()" (keydown.enter)="$event.preventDefault()">
        <!-- Client Name -->
        <div class="mb-4">
          <label class="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-1">
            Client Name <span class="text-red-500">*</span>
          </label>
          <input 
            pInputText 
            formControlName="clientName" 
            placeholder="Enter client name"
            class="w-full"
            [class.ng-invalid]="clientForm.get('clientName')?.invalid && clientForm.get('clientName')?.touched">
          @if (clientForm.get('clientName')?.invalid && clientForm.get('clientName')?.touched) {
            <small class="text-red-500">
              Client name is required
            </small>
          }
        </div>

        <!-- Description -->
        <!-- <div class="mb-4">
          <label class="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-1">
            Description
          </label>
          <textarea 
            pTextarea 
            formControlName="description" 
            placeholder="Enter client description (optional)"
            rows="3"
            class="w-full"
            [autoResize]="true">
          </textarea>
        </div> -->

        <!-- Scopes -->
        <div class="mb-4">
          <label class="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-1">
            Scopes <span class="text-red-500">*</span>
          </label>
          <div class="flex gap-2">
            <input 
              pInputText 
              [(ngModel)]="scopeInput" 
              [ngModelOptions]="{standalone: true}"
              placeholder="Enter scope (e.g., openid, profile, email)"
              class="flex-1"
              (keyup.enter)="addScope()">
            <button 
              pButton 
              label="Add" 
              size="small" 
              (click)="addScope()"
              type="button"
              severity="secondary"
              outlined>
            </button>
          </div>
          <div class="flex flex-wrap gap-2 mt-2">
            @for (scope of scopes; track scope; let i = $index) {
              <p-chip 
                [label]="scope"
                (onRemove)="removeScope(i)"
                [removable]="true">
              </p-chip>
            }
          </div>
          @if (clientForm.get('scopes')?.invalid && clientForm.get('scopes')?.touched) {
            <small class="text-red-500">
              At least one scope is required
            </small>
          }
        </div>

        <!-- Redirect URIs -->
        <div class="mb-4">
          <label class="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-1">
            Redirect URIs <span class="text-red-500">*</span>
          </label>
          <div class="flex gap-2">
            <input 
              pInputText 
              [(ngModel)]="redirectUriInput" 
              [ngModelOptions]="{standalone: true}"
              placeholder="Enter redirect URI (e.g., https://app.com/callback)"
              class="flex-1"
              (keyup.enter)="addRedirectUri()">
            <button 
              pButton 
              label="Add" 
              size="small" 
              (click)="addRedirectUri()"
              severity="secondary"
              type="button"
              outlined>
            </button>
          </div>
          <div class="flex flex-wrap gap-2 mt-2">
            @for (uri of redirectUris; track uri; let i = $index) {
              <p-chip 
                [label]="uri"
                (onRemove)="removeRedirectUri(i)"
                [removable]="true"
                class="bg-blue-50 dark:bg-blue-950">
              </p-chip>
            }
          </div>
          @if (clientForm.get('redirectUris')?.invalid && clientForm.get('redirectUris')?.touched) {
            <small class="text-red-500">
              At least one redirect URI is required
            </small>
          }
        </div>

        <!-- Authorization Grant Types -->
        <div class="mb-4">
          <label class="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-1">
            Authorization Grant Types <span class="text-red-500">*</span>
          </label>
          <div class="flex gap-2">
            <p-select 
              formControlName="authorizationGrantTypes"
              [options]="grantTypeOptions"
              optionLabel="label"
              appendTo="body"
              optionValue="value"
              placeholder="Select grant type"
              [style]="{ width: '100%' }"
              [class.ng-invalid]="clientForm.get('authorizationGrantTypes')?.invalid && clientForm.get('authorizationGrantTypes')?.touched">
            </p-select>
          </div>
          @if (clientForm.get('authorizationGrantTypes')?.invalid && clientForm.get('authorizationGrantTypes')?.touched) {
            <small class="text-red-500">
              Grant type is required
            </small>
          }
        </div>

        <!-- Post Logout Redirect URIs -->
        <div class="mb-4">
          <label class="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-1">
            Post Logout Redirect URIs
          </label>
          <div class="flex gap-2">
            <input 
              pInputText 
              [(ngModel)]="postLogoutUriInput" 
              [ngModelOptions]="{standalone: true}"
              placeholder="Enter post logout URI (e.g., https://app.com/logout)"
              class="flex-1"
              (keyup.enter)="addPostLogoutUri()">
            <button 
              pButton 
              label="Add" 
              size="small" 
              (click)="addPostLogoutUri()"
              severity="secondary"
              type="button"
              outlined>
            </button>
          </div>
          <div class="flex flex-wrap gap-2 mt-2">
            @for (uri of postLogoutUris; track uri; let i = $index) {
              <p-chip 
                [label]="uri"
                (onRemove)="removePostLogoutUri(i)"
                [removable]="true"
                class="bg-purple-50 dark:bg-purple-950">
              </p-chip>
            }
          </div>
        </div>

        <!-- Token Settings Section -->
        <!-- <div class="mb-4 p-4 bg-surface-50 dark:bg-surface-800 rounded-lg border border-surface-200 dark:border-surface-700">
          <h4 class="text-sm font-medium text-surface-700 dark:text-surface-300 mb-3">Token Settings</h4>
          
          <div class="mb-3">
            <label class="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-1">
              Access Token TTL
            </label>
            <div class="flex gap-2">
              <input 
                pInputText 
                formControlName="accessTokenTtl" 
                placeholder="e.g., 3600s"
                class="flex-1">
              <span class="text-sm text-surface-500 flex items-center">seconds</span>
            </div>
          </div>

          <div class="mb-3">
            <label class="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-1">
              Refresh Token TTL
            </label>
            <div class="flex gap-2">
              <input 
                pInputText 
                formControlName="refreshTokenTtl" 
                placeholder="e.g., 86400s"
                class="flex-1">
              <span class="text-sm text-surface-500 flex items-center">seconds</span>
            </div>
          </div>

          <div class="flex items-center gap-2">
            <p-checkbox 
              formControlName="reuseRefreshTokens"
              >
            </p-checkbox>
            <label class="text-sm text-surface-700 dark:text-surface-300">
              Reuse Refresh Tokens
            </label>
          </div>
        </div> -->

        <!-- Form Actions -->
        <div class="flex justify-end gap-2 pt-4 border-t border-surface-200 dark:border-surface-700">
          <button 
            pButton 
            label="Cancel" 
            severity="secondary" 
            type="button"
            (click)="closeDialog()"
            outlined>
          </button>
          <button 
            pButton 
            label="Create Client" 
            type="submit"
            severity="primary"
            [loading]="submitting()"
            [disabled]="clientForm.invalid || submitting()">
          </button>
        </div>
      </form>
    </p-dialog>
  `
})
export class AddClientDialogComponent {
  @Output() clientAdded = new EventEmitter<ClientDetails>();
  @Output() dialogClose = new EventEmitter<void>();

  visible = true;
  submitting = signal(false);
  
  scopeInput = '';
  scopes: string[] = [];
  
  redirectUriInput = '';
  redirectUris: string[] = [];
  
  postLogoutUriInput = '';
  postLogoutUris: string[] = [];

  grantTypeOptions = [
    { label: 'Authorization Code', value: 'authorization_code' },
    { label: 'Client Credentials', value: 'client_credentials' },
    { label: 'Password', value: 'password' },
    { label: 'Implicit', value: 'implicit' },
    { label: 'Refresh Token', value: 'refresh_token' }
  ];

  clientForm: FormGroup;

  constructor(
    private fb: FormBuilder,
    private clientService: ClientService,
    private messageService: MessageService
  ) {
    this.clientForm = this.fb.group({
      clientName: ['', [Validators.required]],
      description: [''],
      scopes: [[], [Validators.required, Validators.minLength(1)]],
      redirectUris: [[], [Validators.required, Validators.minLength(1)]],
      authorizationGrantTypes: ['', [Validators.required]],
      postLogoutRedirectUris: [[]],
      accessTokenTtl: ['3600s', [Validators.required]],
      refreshTokenTtl: ['86400s', [Validators.required]],
      reuseRefreshTokens: [false]
    });
  }

  addScope() {
    const trimmed = this.scopeInput.trim();
    if (trimmed && !this.scopes.includes(trimmed)) {
      this.scopes.push(trimmed);
      this.scopeInput = '';
      this.clientForm.patchValue({ scopes: this.scopes });
      this.clientForm.get('scopes')?.updateValueAndValidity();
    }
  }

  removeScope(index: number) {
    this.scopes.splice(index, 1);
    this.clientForm.patchValue({ scopes: this.scopes });
    this.clientForm.get('scopes')?.updateValueAndValidity();
  }

  addRedirectUri() {
    const trimmed = this.redirectUriInput.trim();
    if (trimmed && !this.redirectUris.includes(trimmed)) {
      this.redirectUris.push(trimmed);
      this.redirectUriInput = '';
      this.clientForm.patchValue({ redirectUris: this.redirectUris });
      this.clientForm.get('redirectUris')?.updateValueAndValidity();
    }
  }

  removeRedirectUri(index: number) {
    this.redirectUris.splice(index, 1);
    this.clientForm.patchValue({ redirectUris: this.redirectUris });
    this.clientForm.get('redirectUris')?.updateValueAndValidity();
  }

  addPostLogoutUri() {
    const trimmed = this.postLogoutUriInput.trim();
    if (trimmed && !this.postLogoutUris.includes(trimmed)) {
      this.postLogoutUris.push(trimmed);
      this.postLogoutUriInput = '';
      this.clientForm.patchValue({ postLogoutRedirectUris: this.postLogoutUris });
    }
  }

  removePostLogoutUri(index: number) {
    this.postLogoutUris.splice(index, 1);
    this.clientForm.patchValue({ postLogoutRedirectUris: this.postLogoutUris });
  }

  onSubmit() {
    if (this.clientForm.invalid) {
      Object.keys(this.clientForm.controls).forEach(key => {
        this.clientForm.get(key)?.markAsTouched();
      });
      return;
    }

    this.submitting.set(true);
    const formValue = this.clientForm.value;
    
    const payload = {
      clientName: formValue.clientName,
      // description: formValue.description || '',
      redirectUris: formValue.redirectUris,
      postLogoutRedirectUris: formValue.postLogoutRedirectUris || [],
      authorizationGrantTypes: [formValue.authorizationGrantTypes],
      scopes: formValue.scopes,
      // tokenSettings: {
      //   accessTokenTimeToLive: formValue.accessTokenTtl,
      //   refreshTokenTimeToLive: formValue.refreshTokenTtl,
      //   reuseRefreshTokens: formValue.reuseRefreshTokens || false
      // }
    };

    console.log('Creating client with payload:', payload);

    this.clientService.createClient(payload).subscribe({
      next: (response) => {
        this.submitting.set(false);
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: `Client "${response.clientName}" created successfully`
        });
        this.clientAdded.emit(response);
        this.closeDialog();
      },
      error: (error) => {
        this.submitting.set(false);
        console.error('Error creating client:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: error.error?.message || error.message || 'Failed to create client'
        });
      }
    });
  }

  closeDialog() {
    this.visible = false;
    this.dialogClose.emit();
  }

  onHide() {
    this.closeDialog();
  }
}